import { Camera, Upload, Trash2 } from "lucide-react";
import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { useUploadAvatarMutation, useRemoveAvatarMutation } from "@/store/api/userApi";

import { addNotification } from "../../store/slices/uiSlice";

interface AvatarUploadDialogProps {
  children: React.ReactNode;
}

export const AvatarUploadDialog: React.FC<AvatarUploadDialogProps> = ({ children }) => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadAvatar, { isLoading: isUploading }] = useUploadAvatarMutation();
  const [removeAvatar, { isLoading: isRemoving }] = useRemoveAvatarMutation();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type and size
      if (!file.type.startsWith("image/")) {
        dispatch(
          addNotification({
            type: "error",
            title: "Invalid image format",
            message: "Please select an image file",
            duration: 5000,
          })
        );
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        dispatch(
          addNotification({
            type: "error",
            title: "File too large",
            message: "File size must be less than 5MB",
            duration: 5000,
          })
        );
        return;
      }

      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      await uploadAvatar(selectedFile).unwrap();
      dispatch(
        addNotification({
          type: "success",
          title: "Success",
          message: "Avatar updated successfully",
          duration: 5000,
        })
      );
      setOpen(false);
      resetState();
    } catch (_error) {
      dispatch(
        addNotification({
          type: "error",
          title: "Failed",
          message: "Failed to upload avatar",
          duration: 5000,
        })
      );
    }
  };

  const handleRemove = async () => {
    try {
      await removeAvatar().unwrap();
      dispatch(
        addNotification({
          type: "success",
          title: "Success",
          message: "Avatar removed successfully",
          duration: 5000,
        })
      );
      setOpen(false);
    } catch (_error) {
      dispatch(
        addNotification({
          type: "error",
          title: "Failed",
          message: "Failed to remove avatar",
          duration: 5000,
        })
      );
    }
  };

  const resetState = () => {
    setPreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      resetState();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Update Profile Picture</DialogTitle>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Current/Preview Avatar */}
          <div className='flex justify-center'>
            <Avatar className='h-32 w-32'>
              <AvatarImage
                src={preview || user?.profile.avatar || "/placeholder.svg"}
                alt='Avatar preview'
              />
              <AvatarFallback className='text-2xl'>{user?.profile.name.charAt(0)}</AvatarFallback>
            </Avatar>
          </div>

          {/* File Input */}
          <input
            ref={fileInputRef}
            type='file'
            accept='image/*'
            onChange={handleFileSelect}
            className='hidden'
          />

          {/* Action Buttons */}
          <div className='space-y-3'>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant='outline'
              className='w-full'
              disabled={isUploading || isRemoving}
            >
              <Upload className='mr-2 h-4 w-4' />
              {selectedFile ? "Choose Different Image" : "Upload New Photo"}
            </Button>

            {selectedFile && (
              <Button
                onClick={handleUpload}
                className='w-full'
                disabled={isUploading || isRemoving}
              >
                <Camera className='mr-2 h-4 w-4' />
                {isUploading ? "Uploading..." : "Save New Avatar"}
              </Button>
            )}

            {user?.profile.avatar && !selectedFile && (
              <Button
                onClick={handleRemove}
                variant='destructive'
                className='w-full'
                disabled={isUploading || isRemoving}
              >
                <Trash2 className='mr-2 h-4 w-4' />
                {isRemoving ? "Removing..." : "Remove Current Photo"}
              </Button>
            )}
          </div>

          {/* File Info */}
          {selectedFile && (
            <div className='text-center text-sm text-muted-foreground'>
              Selected: {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
